/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DatabricksMessagesDatabricksApplication } from '../models/DatabricksMessagesDatabricksApplication';
import type { DatabricksMessagesDatabricksConnectionStatus } from '../models/DatabricksMessagesDatabricksConnectionStatus';
import type { DatabricksMessagesDatabricksWorkspace } from '../models/DatabricksMessagesDatabricksWorkspace';
import type { SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues } from '../models/SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues';
import type { SystemNetHttpHttpResponseMessage } from '../models/SystemNetHttpHttpResponseMessage';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DatabricksApiService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Checks that Databricks instance is accessible and configured correctly (Authorised Roles: PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @returns DatabricksMessagesDatabricksConnectionStatus OK
     * @throws ApiError
     */
    public getDatabricksApiGetDatabricksStatus(): CancelablePromise<DatabricksMessagesDatabricksConnectionStatus> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/databricks/connection',
        });
    }

    /**
     * Initialise Databricks environment. Creates user groups, compute policy, compute clusters, permissions, unity catalog (Authorised Roles: SuperUser)
     * @returns DatabricksMessagesDatabricksConnectionStatus OK
     * @throws ApiError
     */
    public putDatabricksApiInitialiseDatabricks(): CancelablePromise<DatabricksMessagesDatabricksConnectionStatus> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/connection',
        });
    }

    /**
     * Checks that Databricks enabled application has then necessary parts configured in Databricks (Authorised Roles: PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param applicationId
     * @returns DatabricksMessagesDatabricksApplication OK
     * @throws ApiError
     */
    public getDatabricksApiGetDatabricksApplicationStatus(
        applicationId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksApplication> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/databricks/application',
            query: {
                'applicationId': applicationId,
            },
        });
    }

    /**
     * Provisions a Databricks enabled application on Databricks. Creates development application folder, developer group, cross-development schema, development schemas, and assigns permissions to folder/compute/catalogs (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param applicationId
     * @returns DatabricksMessagesDatabricksApplication OK
     * @throws ApiError
     */
    public putDatabricksApiProvisionApplication(
        applicationId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksApplication> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/application',
            query: {
                'applicationId': applicationId,
            },
        });
    }

    /**
     * Promotes a Databricks enabled application on Databricks.
     * Deletes existing production folder if it exists.
     * Creates production application folder, developer group, cross-development schema, development schemas, and assigns permissions to folder/compute/catalogs.
     * Copies notebooks, cross client data (volumes) from development to production folder. (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param applicationId
     * @returns DatabricksMessagesDatabricksApplication OK
     * @throws ApiError
     */
    public postDatabricksApiPromoteApplication(
        applicationId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksApplication> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/databricks/application/promote',
            query: {
                'applicationId': applicationId,
            },
        });
    }

    /**
     * Adds a new template notebook/.ipynb to a Databricks application, to be used as the template for all workspaces (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param applicationId
     * @returns DatabricksMessagesDatabricksApplication OK
     * @throws ApiError
     */
    public putDatabricksApiAddDefaultApplicationNotebook(
        applicationId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksApplication> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/application/notebooks',
            query: {
                'applicationId': applicationId,
            },
        });
    }

    /**
     * Adds a notebook/.ipynb to a Databricks application, to be used as the template for all workspaces (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param formData
     * @returns DatabricksMessagesDatabricksApplication OK
     * @throws ApiError
     */
    public postDatabricksApiAddApplicationNotebook(
        formData?: {
            formData?: Array<SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues>;
        },
    ): CancelablePromise<DatabricksMessagesDatabricksApplication> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/databricks/application/notebooks',
            formData: formData,
            mediaType: 'multipart/form-data',
        });
    }

    /**
     * Checks the current status of a Databricks workspace linked to a Databricks enabled application (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param workspaceId
     * @returns DatabricksMessagesDatabricksWorkspace OK
     * @throws ApiError
     */
    public getDatabricksApiGetDatabricksWorkspaceStatus(
        workspaceId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksWorkspace> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/databricks/workspace',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     * Sets up Databricks for new workspace. Creates service principal, grants access, creates result reader group, workspace catalog and workspace job run (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param workspaceId
     * @returns DatabricksMessagesDatabricksWorkspace OK
     * @throws ApiError
     */
    public putDatabricksApiCreateDatabricksWorkspace(
        workspaceId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksWorkspace> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/workspace',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     * Syncs Jobs with the Production notebooks (Authorised Roles: PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param workspaceId
     * @returns DatabricksMessagesDatabricksWorkspace OK
     * @throws ApiError
     */
    public putDatabricksApiSyncDatabricksWorkspaceJobs(
        workspaceId?: number,
    ): CancelablePromise<DatabricksMessagesDatabricksWorkspace> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/workspace/jobs/sync',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     * Runs a Job with the Production notebook (Authorised Roles: PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param workspaceId
     * @param jobId
     * @returns number OK
     * @throws ApiError
     */
    public putDatabricksApiRunDatabricksWorkspaceJobs(
        workspaceId?: number,
        jobId?: number,
    ): CancelablePromise<number> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/databricks/workspace/jobs/run',
            query: {
                'workspaceId': workspaceId,
                'jobId': jobId,
            },
        });
    }

    /**
     * Gets a PowerBi datasource file (.pbids) configured to connect to the Databricks instance using a particular users credentials (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param workspaceId
     * @returns SystemNetHttpHttpResponseMessage OK
     * @throws ApiError
     */
    public getDatabricksApiGetDatabricksWorkspacePowerBi(
        workspaceId?: number,
    ): CancelablePromise<SystemNetHttpHttpResponseMessage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/databricks/workspace/powerbi',
            query: {
                'workspaceId': workspaceId,
            },
        });
    }

}
